import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { ApiResponse, PaginatedResponse } from '@/types'

export type ComplaintStatus = 'open' | 'in_review' | 'awaiting_customer' | 'resolved' | 'closed'
export type ComplaintCategory =
  | 'service_quality'
  | 'worker_conduct'
  | 'billing'
  | 'scheduling'
  | 'damage'
  | 'other'
export type ComplaintParticipantRole = 'customer' | 'worker' | 'admin' | 'staff'
export type ComplaintMessageType = 'text' | 'attachment' | 'system'
export type ComplaintAction =
  | 'open'
  | 'start_review'
  | 'assign'
  | 'await_customer'
  | 'resolve'
  | 'close'
  | 'reopen'
export type ComplaintSubjectKind = 'job' | 'booking' | 'enquiry' | 'quotation' | 'invoice'

export interface Complaint {
  id: string | number
  ticket_number?: string | null
  complaint_ref?: string | null
  subject_kind: ComplaintSubjectKind
  subject_id: string | number
  job?: string | number | null
  job_id?: string | number | null
  job_ref?: string | null
  job_title?: string | null
  booking?: string | number | null
  booking_id?: string | number | null
  booking_ref?: string | null
  booking_name?: string | null
  enquiry?: string | number | null
  enquiry_id?: string | number | null
  enquiry_name?: string | null
  enquiry_email?: string | null
  quotation?: string | number | null
  quotation_id?: string | number | null
  quotation_number?: string | null
  invoice?: string | number | null
  invoice_id?: string | number | null
  invoice_no?: string | null
  subject: string
  category: ComplaintCategory
  description?: string
  status: ComplaintStatus
  status_label?: string
  allowed_actions?: ComplaintAction[]
  raised_by?: string | number | null
  raised_by_name?: string | null
  /** Raiser's account email; only returned to staff. */
  raised_by_email?: string | null
  complainant_role?: ComplaintParticipantRole
  assigned_to?: string | number | null
  assigned_to_name?: string | null
  resolution_note?: string | null
  resolved_at?: string | null
  closed_at?: string | null
  message_count?: number
  last_message_at?: string | null
  created_at?: string
  updated_at?: string
  service_instance?: string | number | null
  email_enabled?: boolean
  customer_email?: string | null
  email_thread_id?: string | number | null
  email_reply_address?: string | null
}

export interface ComplaintMessage {
  id: string | number
  complaint: string | number
  sender?: string | number | null
  sender_name?: string | null
  sender_role?: ComplaintParticipantRole
  message_type: ComplaintMessageType
  body?: string | null
  file?: string | null
  file_url?: string | null
  file_type?: 'image' | 'video' | 'audio' | 'other'
  file_name?: string | null
  content_type?: string | null
  source?: string | null
  email_message_id?: string | null
  email_in_reply_to?: string | null
  is_internal?: boolean
  created_at?: string | null
}

export interface ComplaintCreatePayload {
  job_id?: string | number
  booking_id?: string | number
  enquiry_id?: string | number
  quotation_id?: string | number
  invoice_id?: string | number
  subject: string
  category?: ComplaintCategory
  description?: string
  body?: string
}

export interface ComplaintUpdatePayload {
  status?: ComplaintStatus
  assigned_to?: string | number | null
  resolution_note?: string
}

export interface ComplaintFollowUp {
  id: string | number
  complaint: string | number
  action: ComplaintAction
  action_label?: string
  from_status?: ComplaintStatus | ''
  from_status_label?: string
  to_status?: ComplaintStatus | ''
  to_status_label?: string
  note?: string | null
  assigned_to?: string | number | null
  assigned_to_name?: string | null
  performed_by?: string | number | null
  performed_by_name?: string | null
  created_at?: string | null
}

export interface ComplaintActionPayload {
  action: ComplaintAction
  note?: string
  resolution_note?: string
  assigned_to?: string | number | null
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T
  }
  return payload as T
}

export const complaintsApi = {
  list: async (params?: {
    job_id?: string | number
    booking_id?: string | number
    enquiry_id?: string | number
    quotation_id?: string | number
    invoice_id?: string | number
    status?: ComplaintStatus
    category?: ComplaintCategory
    service_instance_id?: string | number
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<Complaint>> => {
    const response = await api.get<ApiResponse<PaginatedResponse<Complaint>>>(
      API_PATHS.complaints.list,
      { params },
    )
    return unwrap(response.data)
  },

  create: async (payload: ComplaintCreatePayload): Promise<Complaint> => {
    const response = await api.post<ApiResponse<Complaint>>(API_PATHS.complaints.create, payload)
    return unwrap(response.data)
  },

  get: async (id: string | number): Promise<Complaint> => {
    const response = await api.get<ApiResponse<Complaint>>(API_PATHS.complaints.detail(id))
    return unwrap(response.data)
  },

  update: async (id: string | number, payload: ComplaintUpdatePayload): Promise<Complaint> => {
    const response = await api.post<ApiResponse<Complaint>>(API_PATHS.complaints.update(id), payload)
    return unwrap(response.data)
  },

  listMessages: async (
    complaintId: string | number,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<ComplaintMessage>> => {
    const response = await api.get<ApiResponse<PaginatedResponse<ComplaintMessage>>>(
      API_PATHS.complaints.messages(complaintId),
      { params: { page_size: 100, ...params } },
    )
    return unwrap(response.data)
  },

  sendMessage: async (payload: {
    complaint_id: string | number
    body: string
    is_internal?: boolean
  }): Promise<ComplaintMessage> => {
    const response = await api.post<ApiResponse<ComplaintMessage>>(
      API_PATHS.complaints.messageAdd,
      payload,
    )
    return unwrap(response.data)
  },

  uploadMessage: async (payload: {
    complaint_id: string | number
    file: File
    body?: string
    is_internal?: boolean
  }): Promise<ComplaintMessage> => {
    const formData = new FormData()
    formData.append('complaint_id', String(payload.complaint_id))
    formData.append('file', payload.file)
    if (payload.body) {
      formData.append('body', payload.body)
    }
    formData.append('is_internal', payload.is_internal ? 'true' : 'false')
    const response = await api.post<ApiResponse<ComplaintMessage>>(
      API_PATHS.complaints.messageUpload,
      formData,
    )
    return unwrap(response.data)
  },

  listFollowUps: async (complaintId: string | number): Promise<{ results: ComplaintFollowUp[] }> => {
    const response = await api.get<ApiResponse<{ results: ComplaintFollowUp[] }>>(
      API_PATHS.complaints.actions(complaintId),
    )
    return unwrap(response.data)
  },

  applyAction: async (
    complaintId: string | number,
    payload: ComplaintActionPayload,
  ): Promise<Complaint> => {
    const response = await api.post<ApiResponse<Complaint>>(
      API_PATHS.complaints.applyAction(complaintId),
      payload,
    )
    return unwrap(response.data)
  },
}
